import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelSelect as _vModelSelect, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6" }
const _hoisted_2 = { class: "bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-2" }
const _hoisted_3 = { class: "flex flex-col gap-6" }
const _hoisted_4 = { class: "bg-primary/10 border-b border-primary/20 p-3 flex items-center justify-between gap-4" }
const _hoisted_5 = { class: "flex-1 flex items-center gap-2" }
const _hoisted_6 = ["onUpdate:modelValue"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "p-4 flex flex-col gap-3" }
const _hoisted_9 = {
  key: 0,
  class: "text-center text-muted-foreground py-4 text-xs border border-dashed rounded"
}
const _hoisted_10 = { class: "flex-1 w-full flex flex-col gap-1" }
const _hoisted_11 = ["onUpdate:modelValue"]
const _hoisted_12 = { class: "flex flex-col gap-1 shrink-0 w-full lg:w-32" }
const _hoisted_13 = { class: "text-[10px] uppercase font-bold text-muted-foreground ml-1 flex items-center gap-1" }
const _hoisted_14 = ["onUpdate:modelValue"]
const _hoisted_15 = { class: "flex-1 w-full flex flex-col gap-1" }
const _hoisted_16 = ["onUpdate:modelValue"]
const _hoisted_17 = ["onClick"]
const _hoisted_18 = { class: "text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0" }

import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Map, Plus, Trash2, RefreshCw, Route, Smile } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

interface JourneyTask {
  name: string
  score: number
  actors: string
}

interface JourneySection {
  name: string
  tasks: JourneyTask[]
}

// Estado visual

export default /*@__PURE__*/_defineComponent({
  __name: 'JourneyEditorDialog',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Interfaces
const journeyTitle = ref('Experiencia de Compra')
const sections = ref<JourneySection[]>([
  {
    name: 'Descubrimiento',
    tasks: [
      { name: 'Buscar producto', score: 5, actors: 'Cliente' },
      { name: 'Navegar opciones', score: 3, actors: 'Cliente' }
    ]
  },
  {
    name: 'Compra',
    tasks: [
      { name: 'Añadir al carrito', score: 6, actors: 'Cliente, Sistema' },
      { name: 'Pago fallido', score: 1, actors: 'Cliente, Banco' }
    ]
  }
])

// Acciones
const addSection = () => {
  sections.value.push({
    name: `Nueva Sección ${sections.value.length + 1}`,
    tasks: []
  })
}

const removeSection = (secIdx: number) => {
  sections.value.splice(secIdx, 1)
}

const addTask = (secIdx: number) => {
  sections.value[secIdx].tasks.push({
    name: 'Nueva Tarea',
    score: 4,
    actors: 'Usuario'
  })
}

const removeTask = (secIdx: number, taskIdx: number) => {
  sections.value[secIdx].tasks.splice(taskIdx, 1)
}

// Generar código desde el estado visual
const generateMermaid = () => {
  let code = `journey\n`
  if (journeyTitle.value.trim()) {
    code += `    title ${journeyTitle.value.trim()}\n`
  }
  
  sections.value.forEach(section => {
    if (section.name.trim()) {
      code += `    section ${section.name.trim()}\n`
    }
    
    section.tasks.forEach(task => {
      const safeName = task.name.trim().replace(/:/g, '-') || 'Tarea'
      const safeActors = task.actors.trim().replace(/:/g, '-')
      
      let taskLine = `      ${safeName}: ${task.score}`
      if (safeActors) {
        taskLine += `: ${safeActors}`
      }
      code += `${taskLine}\n`
    })
  })
  
  editableCode.value = code
}

// Sincronizar desde código a estado visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newSections: JourneySection[] = []
  let currentSection: JourneySection = { name: '', tasks: [] }
  let newTitle = ''
  
  for (const line of lines) {
    const tLine = line.trim()
    if (tLine.startsWith('journey')) {
      continue
    } else if (tLine.startsWith('title ')) {
      newTitle = tLine.replace('title ', '').trim()
    } else if (tLine.startsWith('section ')) {
      if (currentSection.name || currentSection.tasks.length > 0) {
        newSections.push(currentSection)
      }
      currentSection = { name: tLine.replace('section ', '').trim(), tasks: [] }
    } else if (tLine.includes(':')) {
      const parts = tLine.split(':').map(s => s.trim())
      if (parts.length >= 2) {
        currentSection.tasks.push({
          name: parts[0],
          score: parseInt(parts[1]) || 3,
          actors: parts.length > 2 ? parts[2] : ''
        })
      }
    }
  }
  
  if (currentSection.name || currentSection.tasks.length > 0) {
    newSections.push(currentSection)
  }
  
  journeyTitle.value = newTitle
  if (newSections.length > 0) {
    sections.value = newSections
  }
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([journeyTitle, sections], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseVisualEditor, {
    open: __props.open,
    "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('update:open', $event))),
    title: "User Journey (Mapa de Viaje)",
    description: "Documenta la experiencia y el estado de ánimo de un usuario a lo largo de un proceso.",
    showCode: showCode.value,
    "onUpdate:showCode": _cache[3] || (_cache[3] = ($event: any) => (showCode.value = $event)),
    copied: copied.value,
    onCopy: copyToClipboard,
    onSave: saveAndClose
  }, {
    icon: _withCtx(() => [
      _createVNode(_unref(Map), { class: "w-5 h-5 text-primary" })
    ]),
    toolbar: _withCtx(() => [
      _createVNode(_unref(Button), {
        size: "sm",
        onClick: addSection
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Plus), { class: "w-4 h-4 mr-2" }),
          _cache[4] || (_cache[4] = _createTextVNode(" Añadir Etapa", -1))
        ]),
        _: 1
      })
    ]),
    canvas: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[5] || (_cache[5] = _createElementVNode("label", { class: "text-sm font-semibold" }, "Título del Viaje (Journey)", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((journeyTitle).value = $event)),
            class: "border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary font-medium",
            placeholder: "Ej. Experiencia de Onboarding"
          }, null, 512), [
            [_vModelText, journeyTitle.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section, secIdx) => {
            return (_openBlock(), _createElementBlock("div", {
              key: secIdx,
              class: "bg-card border rounded-lg shadow-sm flex flex-col overflow-hidden"
            }, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(Route), { class: "w-4 h-4 text-primary" }),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((section.name) = $event),
                    class: "flex-1 bg-transparent text-sm font-bold focus:outline-none focus:border-b border-primary/50 text-foreground",
                    placeholder: "Nombre de la Etapa (ej. Descubrimiento)"
                  }, null, 8, _hoisted_6), [
                    [_vModelText, section.name]
                  ])
                ]),
                _createElementVNode("button", {
                  onClick: ($event: any) => (removeSection(secIdx)),
                  class: "text-muted-foreground hover:text-destructive transition-colors p-1 bg-background/50 rounded",
                  title: "Eliminar Etapa completa"
                }, [
                  _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                ], 8, _hoisted_7)
              ]),
              _createElementVNode("div", _hoisted_8, [
                (section.tasks.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, " Aún no hay tareas en esta etapa. "))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.tasks, (task, taskIdx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: taskIdx,
                    class: "flex flex-col lg:flex-row items-start lg:items-center gap-3 bg-muted/40 p-3 rounded-md border"
                  }, [
                    _createElementVNode("div", _hoisted_10, [
                      _cache[6] || (_cache[6] = _createElementVNode("label", { class: "text-[10px] uppercase font-bold text-muted-foreground ml-1" }, "Acción / Tarea", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((task.name) = $event),
                        class: "bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:border-primary font-medium w-full",
                        placeholder: "Ej. Crear cuenta"
                      }, null, 8, _hoisted_11), [
                        [_vModelText, task.name]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("label", _hoisted_13, [
                        _createVNode(_unref(Smile), { class: "w-3 h-3" }),
                        _cache[7] || (_cache[7] = _createTextVNode(" Satisfacción (1-7) ", -1))
                      ]),
                      _withDirectives(_createElementVNode("select", {
                        "onUpdate:modelValue": ($event: any) => ((task.score) = $event),
                        class: "bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:border-primary w-full"
                      }, [...(_cache[8] || (_cache[8] = [
                        _createElementVNode("option", { value: 1 }, "1 - Muy Frustrante", -1),
                        _createElementVNode("option", { value: 2 }, "2 - Frustrante", -1),
                        _createElementVNode("option", { value: 3 }, "3 - Descontento", -1),
                        _createElementVNode("option", { value: 4 }, "4 - Neutral", -1),
                        _createElementVNode("option", { value: 5 }, "5 - Contento", -1),
                        _createElementVNode("option", { value: 6 }, "6 - Muy Contento", -1),
                        _createElementVNode("option", { value: 7 }, "7 - Excelente", -1)
                      ]))], 8, _hoisted_14), [
                        [_vModelSelect, task.score]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_15, [
                      _cache[9] || (_cache[9] = _createElementVNode("label", { class: "text-[10px] uppercase font-bold text-muted-foreground ml-1" }, "Actores (Separados por coma)", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((task.actors) = $event),
                        class: "bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:border-primary w-full",
                        placeholder: "Ej. Usuario, Sistema"
                      }, null, 8, _hoisted_16), [
                        [_vModelText, task.actors]
                      ])
                    ]),
                    _createElementVNode("button", {
                      onClick: ($event: any) => (removeTask(secIdx, taskIdx)),
                      class: "mt-5 lg:mt-4 text-muted-foreground hover:bg-destructive hover:text-white transition-colors p-2 rounded-full",
                      title: "Eliminar tarea"
                    }, [
                      _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                    ], 8, _hoisted_17)
                  ]))
                }), 128)),
                _createVNode(_unref(Button), {
                  variant: "outline",
                  size: "sm",
                  class: "mt-2 self-start border-dashed",
                  onClick: ($event: any) => (addTask(secIdx))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(Plus), { class: "w-4 h-4 mr-2" }),
                    _cache[10] || (_cache[10] = _createTextVNode(" Añadir Tarea a Etapa ", -1))
                  ]),
                  _: 1
                }, 8, ["onClick"])
              ])
            ]))
          }), 128))
        ])
      ])
    ]),
    "code-panel": _withCtx(() => [
      _createElementVNode("div", _hoisted_18, [
        _cache[12] || (_cache[12] = _createTextVNode(" Mermaid (Sincronizado) ", -1)),
        _createVNode(_unref(Button), {
          size: "sm",
          variant: "default",
          class: "h-7 text-xs",
          onClick: syncToVisual
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(RefreshCw), { class: "w-3 h-3 mr-2" }),
            _cache[11] || (_cache[11] = _createTextVNode(" Sincronizar a Visual ", -1))
          ]),
          _: 1
        })
      ]),
      _createVNode(MermaidCodeEditor, {
        modelValue: editableCode.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((editableCode).value = $event)),
        class: "flex-1 min-h-0"
      }, null, 8, ["modelValue"])
    ]),
    "live-preview": _withCtx(() => [
      _createVNode(MermaidLivePreview, { code: editableCode.value }, null, 8, ["code"])
    ]),
    _: 1
  }, 8, ["open", "showCode", "copied"]))
}
}

})